<template>
    <div>
        <!-- Simple Mode -->
        <div v-if="simple" class="flex flex-col items-end">
            <Button
                variant="outline"
                size="icon"
                class="mb-2 bg-slate-100 hover:bg-slate-200"
                @click="triggerFileInput"
            >
                <AppIcon name="paperclip" class="h-4 w-4 text-slate-700" />
            </Button>

            <input
                ref="fileInput"
                type="file"
                multiple
                class="hidden"
                accept="image/*,.pdf,.doc,.docx,.txt,.rtf"
                @change="onFileSelect"
            />

            <div v-if="attachmentsProxy.length > 0" class="flex flex-col items-end space-y-1">
                <div
                    v-for="(file, index) in attachmentsProxy"
                    :key="index"
                    class="flex items-center space-x-2 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-700"
                >
                    <AppIcon :name="getFileIcon(file)" class="h-3 w-3 text-slate-500" />
                    <span class="max-w-[8rem] truncate">{{ file.filename }}</span>
                    <button type="button" class="text-slate-400 hover:text-slate-600" @click="removeAttachment(index)">
                        <AppIcon name="times" class="h-3 w-3" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Default Mode -->
        <div v-else>
            <label class="mb-2 block text-sm font-medium text-slate-700">Attachments</label>

            <div
                class="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 p-8 text-center transition-colors hover:border-slate-300"
                :class="{ 'border-indigo-300 bg-indigo-50': isDragging }"
                @click="triggerFileInput"
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="handleDrop"
            >
                <AppIcon name="paperclip" class="mb-2 h-6 w-6 text-slate-400" />
                <p class="mb-1 text-sm text-slate-600">
                    <span class="cursor-pointer text-indigo-600 hover:text-indigo-500">Click to upload</span>
                    or drag and drop
                </p>
                <p class="text-xs text-slate-500">PNG, JPG, PDF, DOC, DOCX - Total upload size: 3 MB</p>
            </div>

            <input
                ref="fileInput"
                type="file"
                multiple
                class="hidden"
                accept="image/*,.pdf,.doc,.docx,.txt,.rtf"
                @change="onFileSelect"
            />

            <div v-if="showSizeMeter" class="mb-2 mt-3">
                <div class="flex items-center justify-between text-xs text-slate-600">
                    <span>Total upload size:</span>
                    <span :class="{ 'text-red-600': totalSize > maxSize * 0.85 }">
                        {{ formatFileSize(totalSize) }} / 3 MB
                    </span>
                </div>
                <div class="mt-1 h-1 w-full rounded-full bg-slate-200">
                    <div
                        class="h-1 rounded-full transition-all duration-300"
                        :class="totalSize > maxSize * 0.85 ? 'bg-red-500' : 'bg-indigo-500'"
                        :style="{ width: Math.min(100, (totalSize / maxSize) * 100) + '%' }"
                    ></div>
                </div>
                <p v-if="hasOptimizedAdditionalSize" class="mt-1 text-xs text-slate-500">
                    Inline images are included at their optimized upload size
                    ({{ formatFileSize(additionalSize) }}, reduced from {{ formatFileSize(additionalOriginalSize) }}).
                </p>
                <p v-else-if="additionalSize > 0" class="mt-1 text-xs text-slate-500">
                    Inline images are included in this total.
                </p>
            </div>

            <div v-if="attachmentsProxy.length > 0" class="mt-3 space-y-2">
                <div
                    v-for="(file, index) in attachmentsProxy"
                    :key="index"
                    class="flex items-center justify-between rounded-lg bg-slate-50 p-2"
                >
                    <div class="flex items-center space-x-2">
                        <AppIcon :name="getFileIcon(file)" class="h-4 w-4 text-slate-500" />
                        <span class="max-w-[12rem] truncate text-sm text-slate-700">{{ file.filename }}</span>
                        <span class="text-xs text-slate-500">
                            <template v-if="file.original_size && file.original_size > file.size">
                                ({{ formatFileSize(file.size) }}, optimized from
                                {{ formatFileSize(file.original_size) }})
                            </template>
                            <template v-else>({{ formatFileSize(file.size) }})</template>
                        </span>
                    </div>
                    <button type="button" class="text-slate-400 hover:text-slate-600" @click="removeAttachment(index)">
                        <AppIcon name="times" class="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useFileAttachments } from '@/composables/email/useFileAttachments'
import { Button } from '@/components/ui/button'

export default {
    name: 'FileAttachments',

    components: { Button },

    props: {
        attachments: { type: Array, default: () => [] },
        simple: { type: Boolean, default: false },
        additionalSize: { type: Number, default: 0 },
        additionalOriginalSize: { type: Number, default: 0 },
    },

    emits: ['update:attachments'],

    setup(props, { emit }) {
        const fileInput = ref(null)
        const isDragging = ref(false)

        // Create a computed proxy for two-way binding
        const attachmentsProxy = computed({
            get: () => props.attachments,
            set: (val) => emit('update:attachments', val),
        })

        // Use the updated composable that takes the reactive reference directly
        const { processFiles, formatFileSize, getFileIcon, getTotalAttachmentSize } = useFileAttachments(
            attachmentsProxy,
            emit,
            {
                getAdditionalSize: () => props.additionalSize,
            },
        )

        const maxSize = 3 * 1024 * 1024
        const totalSize = computed(() => getTotalAttachmentSize())
        const showSizeMeter = computed(() => attachmentsProxy.value.length > 0 || props.additionalSize > 0)
        const hasOptimizedAdditionalSize = computed(() => props.additionalOriginalSize > props.additionalSize)

        const triggerFileInput = () => fileInput.value?.click()

        const onFileSelect = async (e) => {
            const files = Array.from(e.target.files || [])
            if (!files.length) return
            await processFiles(files)
            e.target.value = ''
        }

        const handleDrop = async (e) => {
            const files = Array.from(e.dataTransfer?.files || [])
            if (!files.length) return
            await processFiles(files)
        }

        const onDragOver = (e) => {
            e.dataTransfer.dropEffect = 'copy'
            isDragging.value = true
        }

        const onDragLeave = () => {
            isDragging.value = false
        }

        const removeAttachment = (index) => {
            const updated = [...attachmentsProxy.value]
            updated.splice(index, 1)
            attachmentsProxy.value = updated
        }

        return {
            fileInput,
            isDragging,
            attachmentsProxy,
            formatFileSize,
            getFileIcon,
            totalSize,
            maxSize,
            showSizeMeter,
            hasOptimizedAdditionalSize,
            triggerFileInput,
            onFileSelect,
            handleDrop,
            onDragOver,
            onDragLeave,
            removeAttachment,
        }
    },
}
</script>
